import * as React from 'react';
import { useState } from 'react';
import propz from 'propz';
import { Integration } from '../../models/Integration';
import { getFileName, getIntegrationCalendarPublicSiteLink } from '../link/link';
import { INTEGRATION_TYPE_SERVER_TO_CLIENT_MAPPING } from '../../consts/integration';
import { SchoolEvent } from '../../models/event';
import { Link } from 'react-router-dom';
import { CLASHES, CLASHES_STATUS_COLOR } from '../../consts/player';
import { SchoolFile } from '../../models/schoolFile';
import { DeleteIcon } from 'Src/components/SvgIcons/DeleteIcon';
import { AddIcon } from 'Src/components/SvgIcons/AddIcon';
import { EditIcon } from 'Src/components/SvgIcons/EditIcon';
import { CloseIcon } from 'Src/components/SvgIcons/CloseIcon';
import { DetailsIcon } from 'Src/components/SvgIcons/DetailsIcon';
import { TeamsIcon } from 'Src/components/SvgIcons/TeamsIcon';
import { EventsIcon } from 'Src/components/SvgIcons/EventsIcon';
import { CELL_ACTION_ICON } from 'Src/consts/cell';
import { PLAYER_GENDER_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/common';
import { getSchoolEventAges } from 'Src/helpers/event/event';
import { getAgesGroupFormatted } from 'Src/helpers/accessor/accessor';
import { getFullName } from 'Src/helpers/autocomplete/autocomplete';
import { EventParticipants } from 'Src/views/GenericView/AdminSUView/HighLevelTournaments/HighLevelTournamentEvents/EventParticipants/EventParticipants';

export function getColor(house) {
  const result =
    house.colors.length !== 0
      ? house.colors.map((color, clrKey) => {
          return <div key={`color_${clrKey}`} className="eDataList_listItemColor" style={{ background: color }} />;
        })
      : null;

  return <td key={house.id}>{result}</td>;
}

export function getTournamentTagColor(tournamentTag) {
  return <div className="color-thumbnail" style={{ background: tournamentTag.tagColor }}></div>;
}

export function getColorClashes(student, onCellClick) {
  let color;
  const clashesStatus = propz.get(student, ['clashes', 'status'], '');

  const isNoClash = clashesStatus === CLASHES.NO_CLASH;
  const isIntervalClash = clashesStatus === CLASHES.INTERVAL_CLASH;
  const isFullClash = clashesStatus === CLASHES.FULL_CLASH;

  switch (true) {
    case isNoClash:
      color = CLASHES_STATUS_COLOR.NO_CLASH;
      break;
    case isIntervalClash:
      color = CLASHES_STATUS_COLOR.INTERVAL_CLASH;
      break;
    case isFullClash:
      color = CLASHES_STATUS_COLOR.FULL_CLASH;
      break;
    default:
      color = CLASHES_STATUS_COLOR.DEFAULT;
  }

  const onClick = event => {
    event.stopPropagation();
    onCellClick(student);
  };

  return isNoClash ? (
    <span key={`color_${color}`} className="eDataList_listItemColor" style={{ background: color }} />
  ) : (
    <span onClick={onClick} key={`color_${color}`} className="eDataList_listItemColor" style={{ background: color }} />
  );
}

export function getIntegrationName(integration: Integration) {
  if (integration.type === INTEGRATION_TYPE_SERVER_TO_CLIENT_MAPPING.iCalendar) {
    const integrationUrl = getIntegrationCalendarPublicSiteLink(integration);
    return (
      <td key={integration.id}>
        <a href={integrationUrl} target="_blank">
          {integrationUrl}{' '}
        </a>
      </td>
    );
  } else {
    const name = integration.name;
    return typeof name !== 'undefined' ? <td key={integration.id}>{name}</td> : <td />;
  }
}

export function getFileNameWithLink(schoolFile: SchoolFile) {
  const fileName = getFileName(schoolFile);
  const url = schoolFile.url;
  return (
    <td key={schoolFile.id}>
      <a href={url} target="_blank">
        {fileName}
      </a>
    </td>
  );
}

export function getEventNameWithLink(event: SchoolEvent, options) {
  const { user } = options;
  const { activeSchoolId } = user;
  const eventName = propz.get(event, ['generatedNames', activeSchoolId], '');

  return (
    <td key={event.id}>
      <Link to={`/events/event?id=${event.id}`}>{eventName}</Link>
    </td>
  );
}

export function getTournamentEventNameWithLink(event: SchoolEvent, options) {
  const { user } = options;
  const { activeSchoolId } = user;
  const eventName = propz.get(event, ['generatedNames', activeSchoolId], '');

  return (
    <td key={event.id}>
      <Link to={`/tournaments/events/event?id=${event.id}`}>{eventName}</Link>
    </td>
  );
}

export function getTeamTournamentEventNameWithLink(event: SchoolEvent, options) {
  const { user } = options;
  if (event.invitedSchools && event.invitedSchools.length == 2) {
    const school1 = event.invitedSchools[0];
    const school2 = event.invitedSchools[1];
    return (
      <td key={event.id}>
        <Link to={`/tournaments/events/event?id=${event.id}`}>
          {school1.name} v {school2.name}
        </Link>
      </td>
    );
  }
  return <span></span>;
}

export function getBody({ id, body }) {
  return (
    <td key={id}>
      <div dangerouslySetInnerHTML={{ __html: body }} />
    </td>
  );
}

//the options parameter is not used in the function, but is needed according to the CustomCellColumnDefinition type
export function getTeamPlayerEventsIcon(player, options, onIconClick) {
  const { user, readOnly = false, modern = false } = options;
  const { events } = player;
  const assignments = Array.isArray(events) ? events : [];
  const relayCount = assignments.filter(event => event.isRelay || event.teamBased).length;
  const counts = modern && options.tournament ? <small className="hltTeamMembers__eventCounts">
    Individual: {assignments.length - relayCount}/{options.tournament.maxIndividualEventsPerStudent || '∞'} · Relay/team: {relayCount}/{options.tournament.maxRelayEventsPerStudent || '∞'}
  </small> : null;

  if (!Array.isArray(events) || events.length === 0) {
    return <td className="mPreWrap">{modern && <span className="hltTeamMembers__unallocated">No events allocated</span>}{counts}</td>;
  }

  return (
    <td className="mPreWrap">
      {counts}
      {events.map(ev => {
        const { gender } = ev;
        const officialName = propz.get(ev, ['generatedNames', 'official'], '');
        const name = modern ? String(ev.shortDescription || '').trim() || officialName : officialName;
        const ages = getAgesGroupFormatted(ev, { user });
        const title = modern ? name : `${name} ${ages} ${PLAYER_GENDER_SERVER_TO_CLIENT_MAPPING[gender]}`;

        const rawPositions =
          (Array.isArray(ev.positions) && ev.positions) ||
          (ev?.sport?.field?.positions && Array.isArray(ev.sport.field.positions) && ev.sport.field.positions) ||
          (ev?.sport?.positions && Array.isArray(ev.sport.positions) && ev.sport.positions) ||
          [];

        const posOptions = rawPositions.map((p: any) => ({
          id: String(p.id || p._id),
          label: p.shortName || p.name
        }));

        const assign =
          ev.assignment ??
          (Array.isArray(ev.players)
            ? ev.players.find((p: any) => String(p.userId) === String(player.userId))
            : undefined);

        const valuePos = assign?.positionId ? String(assign.positionId) : '';
        const valueCaptain = !!assign?.isCaptain;
        const valueSub = !!assign?.sub;

        const fireUpdate = (patch: Partial<{ positionId: string | null; isCaptain: boolean; sub: boolean }>) => {
          onIconClick(
            {
              currentPlayer: player,
              currentEvent: ev,
              newAssignment: {
                positionId: patch.positionId ?? (valuePos || null),
                isCaptain: typeof patch.isCaptain === 'boolean' ? patch.isCaptain : valueCaptain,
                sub: typeof patch.sub === 'boolean' ? patch.sub : valueSub
              }
            },
            CELL_ACTION_ICON.UPDATE_EVENT_ASSIGNMENT
          );
        };

        return (
          <div key={ev.id} className={modern ? 'hltTeamMembers__allocation' : 'p-2 mb-1 rounded bg-light'}>
            <div className="d-flex align-items-center justify-content-between">
              <span className="mr-2">{title}</span>
              {!readOnly && (modern ? <button type="button" className="hltTeamMembers__unallocate" aria-label={`Remove ${name} allocation for ${getFullName(player)}`}
                onClick={() => onIconClick({currentPlayer: player, currentEvent: ev}, CELL_ACTION_ICON.REMOVE_FROM_EVENT)}>Remove</button> : <DeleteIcon
                onClick={() =>
                  onIconClick({ currentPlayer: player, currentEvent: ev }, CELL_ACTION_ICON.REMOVE_FROM_EVENT)
                }
              />)}
            </div>

            {posOptions.length > 0 && (
              <div className="d-flex align-items-center mt-2">
                <select
                  className="form-control form-control-sm mr-2"
                  disabled={readOnly}
                  aria-label={modern ? `Position for ${getFullName(player)} in ${name}` : undefined}
                  value={valuePos}
                  onChange={e => {
                    const next = e.target.value || '';
                    fireUpdate({ positionId: next ? next : null });
                  }}
                >
                  <option value="">— No position —</option>
                  {posOptions.map(opt => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </select>

                <label className="mr-3 mb-0 d-flex align-items-center">
                  <input
                    type="checkbox"
                    className="mr-1"
                    checked={valueCaptain}
                    disabled={readOnly}
                    onChange={e => fireUpdate({ isCaptain: e.target.checked })}
                  />
                  Captain
                </label>

                <label className="mb-0 d-flex align-items-center">
                  <input
                    type="checkbox"
                    className="mr-1"
                    checked={valueSub}
                    disabled={readOnly}
                    onChange={e => fireUpdate({ sub: e.target.checked })}
                  />
                  Sub
                </label>
              </div>
            )}
          </div>
        );
      })}
    </td>
  );
}

//the options parameter is not used in the function, but is needed according to the CustomCellColumnDefinition type
export function getEventParticipantsIcon(event, options, onIconClick) {
  const { user } = options;
  return <EventParticipants user={user} event={event} onClick={onIconClick} showPositions maxVisible={0} />;
}

//the options parameter is not used in the function, but is needed according to the CustomCellColumnDefinition type
export function getTeamPlayerEventsActionIcon(player, options, onIconClick) {
  const data = {
    currentPlayer: player
  };

  return (
    <td className="mPreWrap text-center">
      <AddIcon onClick={() => onIconClick(data, CELL_ACTION_ICON.ADD_TO_EVENT)} />
    </td>
  );
}

//the options parameter is not used in the function, but is needed according to the CustomCellColumnDefinition type
export function getTournamentEventsActionIcon(event, options, onIconClick) {
  const data = { currentEvent: event };

  const isTeamSport = String(propz.get(event, ['sport', 'players'], '')).toUpperCase() === 'TEAM';

  const rawPositions =
    (event?.sport?.field?.positions && Array.isArray(event.sport.field.positions) && event.sport.field.positions) ||
    (event?.sport?.positions && Array.isArray(event.sport.positions) && event.sport.positions) ||
    [];
  const hasPositions = rawPositions.length > 0;

  const hasPlayers = Array.isArray(event?.players) && event.players.length > 0;

  const canEditPositions = isTeamSport && hasPlayers && hasPositions;

  return (
    <td className="mPreWrap text-center">
      <AddIcon onClick={() => onIconClick(data, CELL_ACTION_ICON.ADD_TO_EVENT)} />

      {canEditPositions && (
        <span className="ml-2">
          <EditIcon onClick={() => onIconClick(data, CELL_ACTION_ICON.EDIT_POSITIONS)} />
        </span>
      )}
    </td>
  );
}

//the options parameter is not used in the function, but is needed according to the CustomCellColumnDefinition type
export function getRemovePlayerFromTeamIcon(player, options, onIconClick) {
  const data = {
    currentPlayer: player
  };

  return (
    <td className="mPreWrap text-right">
      <CloseIcon onClick={() => onIconClick(data, CELL_ACTION_ICON.REMOVE_FROM_TEAM)} />
    </td>
  );
}

//the options parameter is not used in the function, but is needed according to the CustomCellColumnDefinition type
export function getLeaveTournamentIcon(tournament, options, onIconClick) {
  const data = {
    tournamentUsingActionIcon: tournament
  };

  return (
    <td className="mPreWrap text-right">
      <CloseIcon onClick={() => onIconClick(data, CELL_ACTION_ICON.LEAVE_TOURNAMENT)} />
    </td>
  );
}

//the options parameter is not used in the function, but is needed according to the CustomCellColumnDefinition type
export function getTournamentActionsIcons(tournament, options, onIconClick) {
  const data = {
    tournamentUsingActionIcon: tournament
  };

  return (
    <td className="mPreWrap text-left">
      <div className="bTournamentsUpcomingActions">
        <div
          className="eTournamentsUpcomingActionsItem"
          onClick={() => onIconClick(data, CELL_ACTION_ICON.TOURNAMENT_DETAILS)}
        >
          <DetailsIcon />
          <span>Details</span>
        </div>
        <div
          className="eTournamentsUpcomingActionsItem"
          onClick={() => onIconClick(data, CELL_ACTION_ICON.TOURNAMENT_TEAMS)}
        >
          <TeamsIcon />
          <span>Teams</span>
        </div>
        <div
          className="eTournamentsUpcomingActionsItem"
          onClick={() => onIconClick(data, CELL_ACTION_ICON.TOURNAMENT_EVENTS)}
        >
          <EventsIcon />
          <span>Events</span>
        </div>
      </div>
    </td>
  );
}

//the options parameter is not used in the function, but is needed according to the CustomCellColumnDefinition type
export function getDetailsActionIcon(tournament, options, onIconClick) {
  const data = {
    tournamentUsingActionIcon: tournament
  };

  return (
    <td className="mPreWrap text-left">
      <div className="bTournamentsUpcomingActions">
        <div
          className="eTournamentsUpcomingActionsItem"
          onClick={() => onIconClick(data, CELL_ACTION_ICON.TOURNAMENT_DETAILS)}
        >
          <DetailsIcon />
          <span>Details</span>
        </div>
      </div>
    </td>
  );
}

export function getEditPlayerIcon(player, options, onIconClick) {
  const data = {
    currentPlayer: player
  };

  return (
    <td className="mPreWrap text-right">
      <EditIcon onClick={() => onIconClick(data, CELL_ACTION_ICON.EDIT)} />
    </td>
  );
}

